import { DataCollection } from '@esportal/portal-service';
import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Richtext,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useRef, useState } from 'react';
import { PortalContext } from '../../context/PortalContext';
import { formatOwnership } from '../../tracking/trackingHelper';
import { useTrackingManager } from '../../tracking/useTrackingManager';
import { DataCollectionProgressBar } from '../dataCollection/data-collection-progress-bar';
import { CloseFormLayer } from '../dataCollection/layer/closeFormLayer';
import { DataCollectionLayer } from '../dataCollection/layer/dataCollectionLayer';
import { dataCollectionProgressBarBackgroundColor } from '../../helpers/color-tokens';

export const DataCollectionContainer = ({
	dataCollection,
	isJourneyLayerVisible,
}: {
	dataCollection: DataCollection;
	isJourneyLayerVisible: boolean;
}) => {
	const { portalService } = React.useContext(PortalContext);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const dataCollectionLayerRef = useRef<LayerHandleV2<any>>(null);
	const closeFormLayerRef = useRef<LayerHandleV2<any>>(null);
	const trackingManager = useTrackingManager();

	const [progress, setProgress] = useState(0);
	const [isDataCollectionLayerVisible, setIsDataCollectionLayerVisible] = useState<boolean>(false);
	const [isCloseFormLayerVisible, setIsCloseFormLayerVisible] = useState<boolean>(false);

	let userLevel = { level: 0, userLevelName: '', copy: '', gradientColor: '' };
	switch (dataCollection?.userLevel) {
		case 0:
			userLevel = {
				level: 0,
				userLevelName: 'Colecciona curvas',
				copy: '<b>Completa tu perfil para subir de nivel</b>',
				gradientColor: dataCollectionProgressBarBackgroundColor.level0,
			};
			break;
		case 1:
			userLevel = {
				level: 1,
				userLevelName: 'Descubre carreteras',
				copy: 'Sigue completando tu perfil para convertirte en <b>Road lover</b>',
				gradientColor: dataCollectionProgressBarBackgroundColor.level1,
			};
			break;
		case 2:
			userLevel = {
				level: 2,
				userLevelName: 'Road lover',
				copy: 'Sigue completando tu perfil para convertirte en <b>Máster del asfalto</b>',
				gradientColor: dataCollectionProgressBarBackgroundColor.level2,
			};
			break;
		case 3:
			userLevel = {
				level: 3,
				userLevelName: 'Máster del asfalto',
				copy: 'Parece que nada puede detenerte.',
				gradientColor: dataCollectionProgressBarBackgroundColor.level3,
			};
			break;
		default:
			break;
	}

	const sortQuestionByUserLevel = () => {
		return dataCollection.answers.filter((answer) => answer.questionLevel === userLevel.level).length;
	};

	useEffect(() => {
		const sortedQuestionByUserLevel = sortQuestionByUserLevel();

		setTimeout(() => {
			if (
				!isJourneyLayerVisible &&
				((userLevel.level !== 3 && sortedQuestionByUserLevel === 0) ||
					!dataCollection.answers ||
					(dataCollection.answers && dataCollection.answers?.length === 0))
			) {
				setProgress(30);
			}

			if (!isJourneyLayerVisible && userLevel.level !== 3 && sortedQuestionByUserLevel > 0) {
				setProgress(70);
			}

			if (!isJourneyLayerVisible && userLevel.level === 3) {
				setProgress(100);
			}
		}, 400);
	}, [dataCollection, isJourneyLayerVisible, userLevel.level]);

	const handleDataCollectionVisibility = () => {
		trackingManager.trackClubVWButtonClick(
			'¡Vamos!',
			{ contentId: 'Open Data Collection layer' },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		setIsDataCollectionLayerVisible(true);
	};

	return (
		<Container
			data-testid="data-collection-container"
			wrap={'always'}
			gutter={ContainerGutter.dynamic0050}
			padding={{
				top: ContainerPadding.static450,
				right: ContainerPadding.dynamic0130,
				bottom: ContainerPadding.static450,
				left: ContainerPadding.dynamic0130,
			}}
		>
			<Container wrap={'always'} gutter={ContainerGutter.dynamic0130}>
				<Container wrap={'auto'} gutter={ContainerGutter.static100}>
					<Text appearance={TokenTextAppearance.headline400}>Ya eres </Text>{' '}
					<Text appearance={TokenTextAppearance.headline400} bold>
						{userLevel.userLevelName}
					</Text>
				</Container>
				<DataCollectionProgressBar progress={progress} gradientColor={userLevel.gradientColor} />
			</Container>
			<Container wrap={'always'} gutter={ContainerGutter.static350}>
				<Text appearance={TokenTextAppearance.bigcopy100}>
					{(progress === 30 || userLevel.level === 3) && <Richtext markdown={userLevel.copy} dangerouslyParseRawHTML />}
					{progress > 30 && userLevel.level !== 3 && (
						<>
							¿Quieres detenerte aquí? ¡Estás a solo unos pasos de completar tu perfil! Cada respuesta cuenta para
							mejorar tu experiencia.
						</>
					)}
				</Text>
				<Text appearance={TokenTextAppearance.bigcopy100} bold>
					{userLevel.level < 3 ? '¡El siguiente nivel está a un par de curvas!' : '¡Próximamente nuevos retos!'}
				</Text>
				{dataCollection?.userLevel < 3 && (
					<Container wrap={'always'} stretchContent>
						<Text>
							<CTA
								tag="button"
								emphasis="primary"
								aria-label="vamos"
								size="large"
								onClick={handleDataCollectionVisibility}
							>
								<Text bold>¡Vamos!</Text>
							</CTA>
						</Text>
					</Container>
				)}
			</Container>
			<DataCollectionLayer
				dataCollection={dataCollection}
				isDataCollectionLayerVisible={isDataCollectionLayerVisible}
				setIsCloseFormLayerVisible={setIsCloseFormLayerVisible}
				setIsDataCollectionLayerVisible={setIsDataCollectionLayerVisible}
				dataCollectionLayerRef={dataCollectionLayerRef}
			/>
			<CloseFormLayer
				setIsCloseFormLayerVisible={setIsCloseFormLayerVisible}
				isCloseFormLayerVisible={isCloseFormLayerVisible}
				setIsDataCollectionLayerVisible={setIsDataCollectionLayerVisible}
				closeFormLayerRef={closeFormLayerRef}
				dataCollectionLayerRef={dataCollectionLayerRef}
			/>
		</Container>
	);
};
